import { useRef, useState } from 'react';
import { useTableGeometryCtx } from './useTableGeometry';
import { fanLayout, fitFan, overlapPx, FAN_FIT_WIDTH } from './cardFan';
import './CommunityCards.css';

const suitSymbols = { h: '♥', d: '♦', c: '♣', s: '♠' };
const suitColors  = { h: '#f0245e', d: '#f0245e', c: '#191e4e', s: '#191e4e' };

// Face-DOWN opponent backs render at this small "mini" width. A REVEALED (face-up) hand instead
// renders at the viewer's OWN full hole-card size + format (FULL, below), so a shown opponent
// hand looks exactly like the hero's own — the cards visibly "grow" as they flip up. The fan math
// scales to whichever width is in play.
const MINI = { w: 26, h: 37, border: 1.5, rank: 11, suit: 10, big: 16 };
// Full size mirrors HoleCards exactly: 48x68, 2px border, the same 30px PLO stack overlap for a
// 3+ card hand and the 14px overlap the hole-cards CSS uses for a 2-card hand — so a revealed hand
// is indistinguishable from the viewer's own.
const FULL = { w: 48, h: 68, border: 2, rank: 16, suit: 14, big: 36, ploOverlap: 30, pairOverlap: 14 };

// Renders a small row of an opponent's cards, anchored above their seat. Face-DOWN (checkered
// card backs) during a live hand render small (MINI), offset to the side so the avatar shows. Once
// REVEALED at showdown they render at the hero's own full size (FULL) — centered over the avatar
// and tucked into the info bubble — so a shown hand reads identically to the viewer's own hand.
// Mini cards fan via cardFan (the 48px CSS overlap would be far too tight at 26px). `dimmed` fades
// the whole row when the seat is out of the hand (folded / sitting out / away).
// highlightIndexes: a Set of POSITIONS in this seat's shown hand to gold-border at showdown.
// Positions, not tokens: two cards can legitimately share a token (the rank/suit-editing
// Power-Ups create duplicates on purpose), and matching by value lights the wrong card.
// (was) highlightCards: a Set of card tokens ("Ah") to gold-border at showdown — the winner's best
// five (their hole cards used). null/empty = no highlight (face-down/losing hands pass nothing).
// cardTargetActive/onCardTarget: while the hero's own ANY/HOLE-scope card-targeting Power-Up
// (Shrinkify) waits on its pick, an opponent's face-down backs become clickable — the hero picks
// blindly by POSITION (the backs stay face-down; only the 0-based index is sent). onCardTarget(index).
const SeatCards = ({ seat, cards, faceDown = false, count = 2, highlightIndexes = null, dimmed = false, cardTargetActive = false, onCardTarget = null }) => {
    const { seatAboveStyle } = useTableGeometryCtx();
    // Failure toast + one-at-a-time guard for a blind opponent-hole pick (mirrors HoleCards).
    const [targetError, setTargetError] = useState(null);
    const targetPickRef = useRef(false);
    const pickCard = (index) => {
        if (!cardTargetActive || !onCardTarget || targetPickRef.current) return;
        targetPickRef.current = true;
        Promise.resolve(onCardTarget(index))
            .catch((e) => {
                setTargetError(typeof e === 'string' && e ? e : 'Could not pick that card');
                setTimeout(() => setTargetError(null), 4000);
            })
            .finally(() => { targetPickRef.current = false; });
    };

    const parsed = faceDown ? null : (cards ? cards.trim().split(/\s+/).filter(Boolean) : []);
    const n = faceDown ? count : parsed.length;
    if (n <= 0) return null;
    const multi = n > 2;

    // A face-down hand is the small opponent form; a revealed (face-up) hand takes the hero's full
    // size + format so a shown hand grows to match the viewer's own.
    const S = faceDown ? MINI : FULL;
    const ratio = S.h / S.w;

    // Face-DOWN cards stay offset to the RIGHT, overlapping only the edge of the avatar so the
    // profile picture is still visible. A REVEALED hand switches to the hero's own layout —
    // centered over the avatar with the 2-card hand's bottom edge tucked behind the info bubble
    // (hideBottomEdge) — matching HoleCards exactly. anchorBottom keeps the row hugging the bubble
    // in both cases instead of floating above it.
    const positionStyle = faceDown
        ? seatAboveStyle(seat, { center: false, anchorBottom: true })
        : seatAboveStyle(seat, { center: true, anchorBottom: true, hideBottomEdge: true });
    if (!positionStyle) return null;

    // Fan overlap: face-down backs / the mini form keep the count-scaled default (raise for 4/5,
    // shrink for 6+); a full revealed hand uses the same overlaps HoleCards does (30px PLO stack
    // for 3+ cards, 14px for a 2-card hand). fitFan then constrains the fan to the info bubble and
    // fanLayout gets the fitted width so the layout stays consistent.
    const baseOverlap = faceDown
        ? overlapPx(n, MINI.w)
        : (multi ? FULL.ploOverlap : FULL.pairOverlap);
    const fit = fitFan(n, S.w, S.border, FAN_FIT_WIDTH, baseOverlap);
    const layout = fanLayout(n, fit.cardWidth, { overlap: fit.overlap });

    // Size the card + border + fonts inline only when a shrink is needed (fit.scale < 1, i.e. a 6+
    // card PLO hand); otherwise emit nothing so the base/mini CSS governs and every already-fitting
    // count stays pixel-identical.
    const shrunk = fit.scale !== 1;
    const sizeStyle = shrunk ? {
        width: `${fit.cardWidth}px`,
        height: `${fit.cardWidth * ratio}px`,
        borderWidth: `${S.border * fit.scale}px`,
    } : null;
    const fontStyle = (base) => (shrunk ? { fontSize: `${base * fit.scale}px` } : undefined);

    const cardStyle = (i, extra) => ({
        transform: `rotate(${layout.cards[i].rotate}deg)`,
        marginLeft: `${layout.cards[i].marginLeft}px`,
        ...sizeStyle,
        ...extra,
    });

    // The `mini` class carries the small (26px) sizing; a revealed full-size hand drops it so the
    // base .card CSS (48x68) and the .seat-cards:not(.mini) fan rules apply.
    const containerClass = `seat-cards${faceDown ? ' mini' : ''}${dimmed ? ' player-dimmed' : ''}`;

    if (faceDown) {
        return (
            <div className={containerClass} style={positionStyle}>
                {targetError && <div className="discard-error">{targetError}</div>}
                {Array.from({ length: n }, (_, i) => (
                    <div
                        key={i}
                        className={`card card-back${cardTargetActive ? ' card-targetable' : ''}`}
                        style={cardStyle(i)}
                        onClick={cardTargetActive ? () => pickCard(i) : undefined}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className={containerClass} style={positionStyle}>
            {parsed.map((card, i) => {
                const rank = card.slice(0, -1);
                const suit = card.slice(-1);
                return (
                    <div
                        key={i}
                        className={`card card-face${highlightIndexes?.has(i) ? ' card-winner' : ''}`}
                        style={cardStyle(i, { color: suitColors[suit] || '#191e4e' })}
                    >
                        <div className="card-rank" style={fontStyle(S.rank)}>{rank}</div>
                        <div className="card-suit" style={fontStyle(S.suit)}>{suitSymbols[suit] || suit}</div>
                        <div className="card-rank-big" style={fontStyle(S.big)}>{rank}</div>
                    </div>
                );
            })}
        </div>
    );
};

export default SeatCards;
